import LoadSpinner from 'frontend/commons/spinner';
import ErrorSnackbar from 'frontend/components/ErrorSnackbar';
import FlightCard from 'frontend/components/flights/FlightCard';
import { useFlightContext } from 'frontend/contexts/FlightDetailsContext';
import { useFlightsFilters } from 'frontend/contexts/FlightsFiltersContext';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import PackagesApi from '../../../api/paquetes';
import Flex from '../../../commons/Flex';
import { getCacheStatus } from 'frontend/helpers/getCacheStatus';
import { useRefreshModal } from 'frontend/hooks/useRefreshModal';
import RefreshModal from 'frontend/components/home/components/RefreshModal';
import { useSelector } from 'react-redux';
import { CACHE_DEFAULTS, SESSION_STORAGE_KEYS } from 'frontend/constants';

const FlightsListView = () => {
  const { t } = useTranslation('layout');
  const { setData, filteredData, isLoading, setIsLoading, error, setError, filters } =
    useFlightsFilters();
  const { search } = useLocation();
  const { flightInfo, setFlightInformation, handleCacheExpiration } = useFlightContext();
  const [errorMessage, setErrorMessage] = useState('');
  const [openToast, setOpenToast] = useState(false);
  const delegation = useSelector((state) => state.applicationState.delegation);
  const { showRefreshModal, setShowRefreshModal, handleRefreshConfirm } = useRefreshModal();

  const searchParamsToObject = useMemo(() => {
    const params = new URLSearchParams(search);
    const filters = {};

    params.forEach((value, key) => {
      if (key === 'passengers') {
        filters[key] = value.split(',').map(Number);
      } else {
        filters[key] = value;
      }
    });

    return filters;
  }, [search]);

  const showError = useCallback((message) => {
    setErrorMessage(message);
    setOpenToast(true);
  }, []);

  const handleCloseToast = useCallback(() => {
    setOpenToast(false);
  }, []);

  const handleLlegoData = useCallback(async () => {
    const {
      destinationId,
      destinationName,
      originId,
      originName,
      departureDate,
      returnDate,
      passengers,
    } = searchParamsToObject;

    if (getCacheStatus() === true) {
      window.sessionStorage.setItem(SESSION_STORAGE_KEYS.CACHE_STATUS, 'true');
    }

    const cacheValue = getCacheStatus();

    const data = {
      originId: originId,
      destinationID: destinationId,
      origin: originName,
      destination: destinationName,
      passengers,
      departureDate,
      returnDate,
      direct: false,
      baggage: false,
      cabin: filters.cabinClass,
      currency: filters.currency,
      cache: cacheValue,
      delegation,
    };

    setFlightInformation({
      ...flightInfo,
      destinationId,
      destinationName,
      originId,
      originName,
      departureDate,
      returnDate,
      passengers,
    });

    try {
      setIsLoading(true);
      const dataResponse = await PackagesApi.fetchLlegoData(data);
      setData(dataResponse?.data);
      handleCacheExpiration(
        dataResponse?.cacheExpiresAt ||
          new Date(Date.now() + CACHE_DEFAULTS.DEFAULT_EXPIRATION_TIME),
      );
      window.sessionStorage.setItem(SESSION_STORAGE_KEYS.CACHE_STATUS, 'true');
      setError(false);
    } catch (error) {
      showError(`${error?.message}`);
      setError(true);
    } finally {
      setIsLoading(false);
    }
  }, [
    searchParamsToObject,
    filters.cabinClass,
    filters.currency,
    setFlightInformation,
    flightInfo,
    handleCacheExpiration,
    delegation,
  ]);

  const handleRefresh = useCallback(() => {
    handleLlegoData();
  }, [handleLlegoData]);

  useEffect(() => {
    const abortController = new AbortController();

    handleLlegoData();

    return () => {
      abortController.abort();
    };
  }, [handleLlegoData]);

  if (isLoading) {
    return (
      <div className="container package__list-view__area loading-packages">
        <h2>Buscando los mejores vuelos para ti</h2>
        <p style={{ textAlign: 'center', padding: '0 2rem' }}>Esto puede tomar unos segundos...</p>
        <LoadSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="container package__list-view__area package__list-view__area--error">
        <h2>¡Ups! Algo salió mal</h2>
        <p style={{ textAlign: 'center', padding: '0 2rem' }}>
          No pudimos cargar los vuelos. Por favor, intenta nuevamente en unos momentos o ajuste los
          filtros de búsqueda.
        </p>
        <button onClick={handleLlegoData}>Reintentar</button>
        <ErrorSnackbar open={openToast} message={errorMessage} onClose={handleCloseToast} />
      </div>
    );
  }

  if (!filteredData || filteredData.length === 0) {
    return (
      <div className="container package__list-view__area package__list-view__area--empty">
        <h2>No encontramos vuelos disponibles</h2>
        <p style={{ textAlign: 'center', padding: '0 2rem' }}>
          Prueba ajustando los filtros de búsqueda o seleccionando fechas diferentes.
        </p>
      </div>
    );
  }

  return (
    <div className="package__list-view__area">
      <Flex className="package__list__view__wrapper--flight">
        <Flex vertical className="package__list__view__wrapper--flight__results">
          {filteredData.map((flightData, index) => {
            if (!flightData || !flightData.flight || flightData.flight.length === 0) return null;
            return <FlightCard {...flightData} key={`${flightData.currency}-${index}`} />;
          })}
        </Flex>
      </Flex>
      <RefreshModal
        open={showRefreshModal}
        onClose={() => setShowRefreshModal(false)}
        onConfirm={() => handleRefreshConfirm(handleRefresh)}
        disableBackdropClick={true}
        disableEscapeKeyDown={true}
      />
      <ErrorSnackbar open={openToast} message={errorMessage} onClose={handleCloseToast} />
    </div>
  );
};

export default FlightsListView;
